import { FilterService } from 'primeng/api';
import { DateTime } from 'luxon';
import { getAppDateFormatPlaceholder } from '@shared/common/pipes/format.util';

export const TABLE_DATE_EQUALS_MATCH_MODE = 'dateEquals';
export const TABLE_DATE_CONTAINS_MATCH_MODE = 'dateContains';

export function toFilterableDate(value: any): Date | null {
    if (value == null || value === '') {
        return null;
    }
    if (DateTime.isDateTime(value)) {
        if (!value.isValid) {
            return null;
        }
        return new Date(value.year, value.month - 1, value.day);
    }
    if (value instanceof Date && !isNaN(value.getTime())) {
        return new Date(value.getFullYear(), value.getMonth(), value.getDate());
    }
    if (typeof value === 'object' && value.year != null && value.month != null && value.day != null) {
        return new Date(Number(value.year), Number(value.month) - 1, Number(value.day));
    }
    const parsed = new Date(value);
    if (!isNaN(parsed.getTime())) {
        return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
    }
    return null;
}

export function formatDateForDisplay(value: any, placeholder?: string): string {
    if (value == null || value === '') {
        return '';
    }
    if (typeof value === 'string' && !toFilterableDate(value)) {
        return value;
    }
    const date = toFilterableDate(value);
    if (!date) {
        return typeof value === 'string' ? value : '';
    }
    const format = (placeholder || getAppDateFormatPlaceholder()).toUpperCase();
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear());
    return format.replace('DD', day).replace('MM', month).replace('YYYY', year);
}

export function applyTimesheetDateFilterFields(
    item: Record<string, any>,
    fields: string[],
    displaySuffix = 'Display'
): void {
    fields.forEach((field) => {
        item[`${field}${displaySuffix}`] = formatDateForDisplay(item[field]);
    });
}

export function datesAreSameDay(value: any, filter: any): boolean {
    if (filter == null || filter === '') {
        return true;
    }
    const valueDate = toFilterableDate(value);
    const filterDate = toFilterableDate(filter);
    if (!valueDate || !filterDate) {
        return false;
    }
    return valueDate.getTime() === filterDate.getTime();
}

export function parseDisplayDate(
    value: string,
    formatPlaceholder?: string,
    options?: { allowTwoDigitYear?: boolean }
): Date | null {
    const raw = (value || '').trim();
    if (!raw) {
        return null;
    }

    const rawParts = raw.split(/[/\-.]/);
    if (rawParts.length !== 3 || rawParts.some((part) => part === '' || Number.isNaN(parseInt(part, 10)))) {
        return null;
    }

    const format = (formatPlaceholder || getAppDateFormatPlaceholder()).toUpperCase();
    let day: number;
    let month: number;
    let yearToken: string;

    if (format.startsWith('YYYY')) {
        yearToken = rawParts[0];
        if (format.includes('YYYY/DD') || format.includes('YYYY-DD') || format.includes('YYYY.DD')) {
            day = parseInt(rawParts[1], 10);
            month = parseInt(rawParts[2], 10);
        } else {
            month = parseInt(rawParts[1], 10);
            day = parseInt(rawParts[2], 10);
        }
    } else if (format.startsWith('MM')) {
        month = parseInt(rawParts[0], 10);
        day = parseInt(rawParts[1], 10);
        yearToken = rawParts[2];
    } else {
        day = parseInt(rawParts[0], 10);
        month = parseInt(rawParts[1], 10);
        yearToken = rawParts[2];
    }

    let year = parseInt(yearToken, 10);
    if (yearToken.length !== 4) {
        if (!options?.allowTwoDigitYear || yearToken.length !== 2) {
            return null;
        }
        year += 2000;
    }

    const parsed = new Date(year, month - 1, day);
    if (parsed.getFullYear() !== year || parsed.getMonth() !== month - 1 || parsed.getDate() !== day) {
        return null;
    }
    return parsed;
}

export function formatDateForServerFilter(date: Date | null | undefined): string {
    if (!date) {
        return '';
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}T12:00:00`;
}

export const TASK_STATUS_NAMES: Record<number, string> = {
    1: 'New',
    2: 'In progress',
    3: 'Overdue',
    4: 'Completed',
};
export const TASK_PRIORITY_NAMES: Record<number, string> = {
    1: 'High',
    2: 'Moderate',
    3: 'Low',
};
export const TASK_SEVERITY_NAMES: Record<number, string> = {
    1: 'Critical',
    2: 'High',
    3: 'Medium',
    4: 'Low',
};
export const PRODUCTIVITY_TYPE_NAMES: Record<number, string> = {
    1: 'Productive',
    2: 'Unproductive',
    3: 'Neutral',
};
export const WORKING_TYPE_NAMES: Record<number, string> = {
    1: 'Home',
    2: 'Office',
    3: 'Remote',
};

export function applyNamedFilterFields(
    items: any[],
    mappings: { idField: string; nameField: string; names: Record<number, string> }[]
): any[] {
    (items || []).forEach((item) => {
        mappings.forEach((mapping) => {
            item[mapping.nameField] = mapping.names[item[mapping.idField]] ?? '';
        });
    });
    return items;
}

export function registerTableFilterMatchers(filterService: FilterService): void {
    filterService.register(TABLE_DATE_EQUALS_MATCH_MODE, (value, filter): boolean => {
        if (filter === undefined || filter === null || filter === '') {
            return true;
        }
        if (value === undefined || value === null || value === '') {
            return false;
        }
        return datesAreSameDay(value, filter);
    });

    filterService.register(TABLE_DATE_CONTAINS_MATCH_MODE, (value, filter): boolean => {
        if (filter === undefined || filter === null || filter === '') {
            return true;
        }
        const filterText = String(filter).trim().toLowerCase();
        if (!filterText) {
            return true;
        }
        const formatted = formatDateForDisplay(value).toLowerCase();
        if (formatted.includes(filterText)) {
            return true;
        }
        if (typeof value === 'string') {
            return value.toLowerCase().includes(filterText);
        }
        return false;
    });
}
